import React from 'react';
import { Tooltip } from 'antd';

import { LOCK } from '../icons';
import { describeSource } from '../../utils/notificationCopy';

/**
 * The "Set by" column: one word saying WHO decided this row's delivery.
 *
 * A settings cascade that only showed the answer would leave every reader
 * with the same question — *did I turn this off, or did someone above me?*
 * So the mark is a column of its own (104px, `justify-self: start`, one box
 * per row) rather than a tag floating beside the name: the marks line up
 * down the list, and a run of inherited rows is visible at a glance.
 *
 *   Mine / This collection / Platform   the tier you are standing on — an
 *                                       action-tint chip, because it is the
 *                                       one you can take back
 *   Collection / Platform / Default     it fell through to you from there,
 *                                       muted plain words
 *   Required + lock                     a tier above froze it; nothing here
 *                                       is yours to change
 *
 * The two vocabularies never share a word (Mine vs You, This collection vs
 * Collection), so the chip reads without its colour.
 *
 * Any other cascade (a collection's Time zone, which falls from the
 * collection to the one above it to keepr's default) passes its own `word`,
 * `phrase` and `own` instead of a notification row: the same two looks, a
 * chip for "set here" and muted words for "fell through from there". Beside a
 * field, give it an `id` and name it in the control's `aria-describedby`, so
 * the control is heard with where its value came from.
 */
export default function SourceMark({ row, tier, lockReason, word: givenWord, phrase: givenPhrase, own: givenOwn = false, className = '', id, focusable = false }) {
	const { word, phrase, own, locked } = givenWord
		? { word: givenWord, phrase: givenPhrase || givenWord, own: Boolean(givenOwn), locked: false }
		: describeSource(row, tier);
	const reason = locked ? (lockReason || row?.lockReason || null) : null;

	const classes = ['source-mark', own ? 'source-mark--own' : '', locked ? 'source-mark--locked' : '', className]
		.filter(Boolean).join(' ');

	const mark = (
		<span id={id} className={classes} title={focusable ? undefined : (reason || undefined)} tabIndex={focusable ? 0 : undefined}>
			{/* The column caption is aria-hidden (each cell already names its
			    channel), so the mark speaks for itself to a reader hearing the
			    row one node at a time — and it speaks the SENTENCE, which is
			    the same string the phone fold prints. Bolting "Set by " onto
			    the column word gave that reader "Set by Mine". */}
			<span className="visually-hidden">{phrase}</span>
			{locked ? <LOCK className="source-mark__glyph" aria-hidden="true" /> : null}
			<span className="source-mark__word" aria-hidden="true">{word}</span>
		</span>
	);

	// A native title never shows on keyboard focus, so the focusable mark
	// (KPR-1230) tells its full text through a Tooltip on hover AND focus.
	return focusable
		? <Tooltip trigger={['hover', 'focus']} title={word}>{mark}</Tooltip>
		: mark;
}
